<template>
  <div class="admin-master-data">
    <b-alert v-if="!canAccess" variant="warning" show class="mb-0">
      <div class="d-flex align-items-start">
        <feather-icon icon="AlertTriangleIcon" size="18" class="mr-75 flex-shrink-0 mt-25" />
        <div>
          <strong class="d-block mb-25">Permission required</strong>
          <span class="small">You need create or manage permission for Admin Settings.</span>
        </div>
      </div>
    </b-alert>

    <template v-else>
      <b-alert
        v-if="statusMsg"
        :variant="statusVariant"
        show
        dismissible
        class="py-75 mb-1"
        @dismissed="statusMsg = ''"
      >
        <div class="d-flex align-items-center">
          <feather-icon
            :icon="statusVariant === 'success' ? 'CheckCircleIcon' : statusVariant === 'danger' ? 'XCircleIcon' : 'AlertCircleIcon'"
            size="16"
            class="mr-50 flex-shrink-0"
          />
          {{ statusMsg }}
        </div>
      </b-alert>

      <b-row class="admin-master-data__layout" no-gutters>
        <!-- Sub-section nav -->
        <b-col cols="12" md="3" lg="3" xl="2" class="admin-master-data__nav-col pr-md-1">
          <nav class="admin-master-data__nav mb-1 mb-md-0" aria-label="Master data sections">
            <button
              v-for="section in sections"
              :key="section.key"
              type="button"
              class="admin-master-data__nav-item"
              :class="{ 'admin-master-data__nav-item--active': activeSection === section.key }"
              :aria-current="activeSection === section.key ? 'page' : null"
              @click="selectSection(section.key)"
            >
              <feather-icon :icon="section.icon" size="15" class="mr-50 flex-shrink-0" />
              <span class="admin-master-data__nav-label">{{ section.title }}</span>
              <b-badge
                v-if="sectionCount(section.key)"
                variant="light-secondary"
                class="admin-master-data__nav-badge ml-auto"
              >
                {{ sectionCount(section.key) }}
              </b-badge>
            </button>
          </nav>

          <b-button
            v-b-toggle.master-data-api-help
            variant="link"
            size="sm"
            class="admin-master-data__help-toggle p-0 d-none d-md-inline-flex align-items-center"
          >
            <feather-icon icon="InfoIcon" size="14" class="mr-25" />
            API & permissions
          </b-button>
          <b-collapse id="master-data-api-help" class="d-none d-md-block">
            <b-alert variant="light" show class="small py-75 mt-50 mb-0">
              Changes use <code>POST /api/admin/config</code>, <code>POST /api/jobs/client</code>, and
              <code>POST /api/locations</code>. Requires <strong>create</strong> or <strong>manage</strong> on <code>admin</code>.
            </b-alert>
          </b-collapse>
        </b-col>

        <!-- Section content -->
        <b-col cols="12" md="9" lg="9" xl="10" class="admin-master-data__content-col pl-md-1">
          <!-- Exotel DIDs -->
          <section v-show="activeSection === 'exotel'" aria-labelledby="md-exotel-title">
            <admin-section-header
              id="md-exotel-title"
              title="DID Assignment"
              description="Assign Exotel caller ID (DID) to recruiter emp_id — powers My Calls Start Calling."
              icon="PhoneIcon"
            >
              <template #actions>
                <b-button
                  v-if="canCreate && !showDidForm"
                  size="sm"
                  variant="primary"
                  @click="openDidForm"
                >
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  Add DID
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="loadingDids"
                  title="Refresh list"
                  @click="loadExotelRows"
                >
                  <b-spinner v-if="loadingDids" small />
                  <feather-icon v-else icon="RefreshCwIcon" size="14" />
                </b-button>
              </template>
            </admin-section-header>

            <b-card v-if="canCreate && showDidForm" body-class="p-75 mb-1" class="admin-master-data__form-card">
              <h6 class="admin-master-data__form-title mb-75">
                <feather-icon icon="PlusCircleIcon" size="15" class="mr-25" />
                Assign new DID
              </h6>
              <b-row class="align-items-end">
                <b-col cols="12" md="4" class="mb-50 mb-md-0">
                  <b-form-group
                    label="Employee ID"
                    label-for="did-emp-id"
                    label-class="font-weight-bold"
                    description="Recruiter emp_id, e.g. SH-091"
                    class="mb-0"
                  >
                    <b-form-input
                      id="did-emp-id"
                      v-model="didForm.empId"
                      placeholder="SH-091"
                      :state="didFormTouched && !didForm.empId.trim() ? false : null"
                      @blur="didFormTouched = true"
                      @keyup.enter="saveDid"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4" class="mb-50 mb-md-0">
                  <b-form-group
                    label="Caller ID (DID)"
                    label-for="did-caller-id"
                    label-class="font-weight-bold"
                    description="Exotel outbound number"
                    class="mb-0"
                  >
                    <b-form-input
                      id="did-caller-id"
                      v-model="didForm.callerId"
                      placeholder="09484956746"
                      :state="didFormTouched && !didForm.callerId.trim() ? false : null"
                      @blur="didFormTouched = true"
                      @keyup.enter="saveDid"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <div class="d-flex">
                    <b-button variant="primary" class="flex-grow-1" :disabled="savingDid" @click="saveDid">
                      <b-spinner v-if="savingDid" small class="mr-25" />
                      <feather-icon v-else icon="CheckIcon" size="14" class="mr-25" />
                      Assign DID
                    </b-button>
                    <b-button
                      variant="outline-secondary"
                      class="ml-50"
                      :disabled="savingDid"
                      @click="cancelDidForm"
                    >
                      Cancel
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </b-card>

            <div class="admin-master-data__table-toolbar d-flex flex-wrap align-items-center justify-content-between mb-50">
              <b-form-input
                v-model="didSearch"
                size="sm"
                placeholder="Search by emp ID or caller ID…"
                class="admin-master-data__search"
              />
              <small class="text-muted">{{ filteredExotelRows.length }} of {{ exotelRows.length }}</small>
            </div>

            <b-overlay :show="loadingDids" rounded spinner-small class="admin-master-data__table-wrap">
              <b-table
                v-if="filteredExotelRows.length"
                :items="filteredExotelRows"
                :fields="visibleDidFields"
                small
                responsive
                sticky-header="280px"
                class="admin-master-data__table mb-0"
                thead-class="admin-master-data__thead"
              >
                <template #cell(title)="data">
                  <code class="admin-master-data__emp-chip">{{ data.item.title }}</code>
                </template>
                <template #cell(abbrev)="data">
                  <span class="font-weight-bold">{{ data.item.abbrev }}</span>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    v-if="canDelete"
                    size="sm"
                    variant="outline-danger"
                    :disabled="deletingId === data.item.id"
                    title="Remove assignment"
                    @click="removeRow(data.item)"
                  >
                    <b-spinner v-if="deletingId === data.item.id" small />
                    <feather-icon v-else icon="Trash2Icon" size="14" />
                  </b-button>
                </template>
              </b-table>
              <admin-empty-state
                v-else-if="!loadingDids"
                icon="PhoneOffIcon"
                :title="didSearch ? 'No matches' : 'No DIDs configured'"
                :description="didSearch ? 'Try a different search term.' : 'Click Add DID to assign a caller ID and enable Start Calling for recruiters.'"
              />
            </b-overlay>
          </section>

          <!-- Clients -->
          <section v-show="activeSection === 'clients'" aria-labelledby="md-clients-title">
            <admin-section-header
              id="md-clients-title"
              title="Clients"
              description="Client names used across jobs, invoices, and reporting."
              icon="BriefcaseIcon"
            />

            <b-card v-if="canCreate" body-class="p-75 mb-1" class="admin-master-data__form-card">
              <h6 class="admin-master-data__form-title mb-75">
                <feather-icon icon="PlusCircleIcon" size="15" class="mr-25" />
                Add client
              </h6>
              <b-row class="align-items-end">
                <b-col cols="12" md="8" class="mb-50 mb-md-0">
                  <b-form-group
                    label="Client name"
                    label-for="client-name"
                    label-class="font-weight-bold small"
                    class="mb-0"
                  >
                    <b-form-input
                      id="client-name"
                      v-model="clientForm.name"
                      placeholder="e.g. GPS Renewables"
                      :state="clientFormTouched && !clientForm.name.trim() ? false : null"
                      @keyup.enter="saveClient"
                      @blur="clientFormTouched = true"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-button variant="primary" block :disabled="savingClient" @click="saveClient">
                    <b-spinner v-if="savingClient" small class="mr-25" />
                    Add client
                  </b-button>
                </b-col>
              </b-row>
            </b-card>

            <div class="admin-master-data__table-toolbar d-flex flex-wrap align-items-center justify-content-between mb-50">
              <b-form-input
                v-model="clientSearch"
                size="sm"
                placeholder="Search clients…"
                class="admin-master-data__search"
              />
              <small class="text-muted">{{ filteredClients.length }} of {{ clients.length }}</small>
            </div>

            <b-overlay :show="loadingClients" rounded spinner-small class="admin-master-data__table-wrap">
              <b-table
                v-if="filteredClients.length"
                :items="filteredClients"
                :fields="clientFields"
                small
                responsive
                sticky-header="280px"
                class="admin-master-data__table mb-0"
              >
                <template #cell(title)="data">
                  {{ masterDataLabel(data.item, ['title', 'client_name', 'name']) }}
                </template>
              </b-table>
              <admin-empty-state
                v-else-if="!loadingClients"
                icon="BriefcaseIcon"
                :title="clientSearch ? 'No matches' : 'No clients yet'"
                :description="clientSearch ? 'Try a different search term.' : 'Add your first client using the form above.'"
              />
            </b-overlay>
          </section>

          <!-- Locations -->
          <section v-show="activeSection === 'locations'" aria-labelledby="md-locations-title">
            <admin-section-header
              id="md-locations-title"
              title="Locations"
              description="Cities and locations used in job postings and candidate filters."
              icon="MapPinIcon"
            />

            <b-card v-if="canCreate" body-class="p-75 mb-1" class="admin-master-data__form-card">
              <h6 class="admin-master-data__form-title mb-75">
                <feather-icon icon="PlusCircleIcon" size="15" class="mr-25" />
                Add location
              </h6>
              <b-row class="align-items-end">
                <b-col cols="12" md="8" class="mb-50 mb-md-0">
                  <b-form-group
                    label="Location / city"
                    label-for="location-name"
                    label-class="font-weight-bold small"
                    class="mb-0"
                  >
                    <b-form-input
                      id="location-name"
                      v-model="locationForm.name"
                      placeholder="e.g. Mumbai"
                      :state="locationFormTouched && !locationForm.name.trim() ? false : null"
                      @keyup.enter="saveLocation"
                      @blur="locationFormTouched = true"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-button variant="primary" block :disabled="savingLocation" @click="saveLocation">
                    <b-spinner v-if="savingLocation" small class="mr-25" />
                    Add location
                  </b-button>
                </b-col>
              </b-row>
            </b-card>

            <div class="admin-master-data__table-toolbar d-flex flex-wrap align-items-center justify-content-between mb-50">
              <b-form-input
                v-model="locationSearch"
                size="sm"
                placeholder="Search locations…"
                class="admin-master-data__search"
              />
              <small class="text-muted">{{ filteredLocations.length }} of {{ locations.length }}</small>
            </div>

            <b-overlay :show="loadingLocations" rounded spinner-small class="admin-master-data__table-wrap">
              <b-table
                v-if="filteredLocations.length"
                :items="filteredLocations"
                :fields="locationFields"
                small
                responsive
                sticky-header="280px"
                class="admin-master-data__table mb-0"
              >
                <template #cell(title)="data">
                  {{ masterDataLabel(data.item, ['title', 'name']) }}
                </template>
              </b-table>
              <admin-empty-state
                v-else-if="!loadingLocations"
                icon="MapPinIcon"
                :title="locationSearch ? 'No matches' : 'No locations yet'"
                :description="locationSearch ? 'Try a different search term.' : 'Add cities and locations using the form above.'"
              />
            </b-overlay>
          </section>

          <!-- Job master data -->
          <section v-show="activeSection === 'jobs'" aria-labelledby="md-jobs-title">
            <admin-section-header
              id="md-jobs-title"
              title="Job master data"
              description="Dropdown values for job category, vertical, payroll, type, and status."
              icon="LayersIcon"
            >
              <template #actions>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="loadingJobRows"
                  @click="loadJobRows"
                >
                  <b-spinner v-if="loadingJobRows" small />
                  <feather-icon v-else icon="RefreshCwIcon" size="14" class="mr-25" />
                  Reload
                </b-button>
              </template>
            </admin-section-header>

            <b-card v-if="canCreate" body-class="p-75 mb-1" class="admin-master-data__form-card">
              <h6 class="admin-master-data__form-title mb-75">
                <feather-icon icon="PlusCircleIcon" size="15" class="mr-25" />
                Add dropdown value
              </h6>
              <b-row class="align-items-end">
                <b-col cols="12" md="4" class="mb-50">
                  <b-form-group label="Type" label-class="font-weight-bold small" class="mb-0">
                    <b-form-select v-model="jobForm.paramName" :options="jobParamOptions" @change="loadJobRows" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4" class="mb-50">
                  <b-form-group
                    label="Title"
                    label-for="job-title"
                    label-class="font-weight-bold small"
                    description="Display label shown in dropdowns"
                    class="mb-0"
                  >
                    <b-form-input
                      id="job-title"
                      v-model="jobForm.title"
                      placeholder="Display label"
                      :state="jobFormTouched && !jobForm.title.trim() ? false : null"
                      @blur="jobFormTouched = true"
                      @keyup.enter="saveJobRow"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="2" class="mb-50">
                  <b-form-group label="Abbrev" label-for="job-abbrev" label-class="font-weight-bold small" class="mb-0">
                    <b-form-input id="job-abbrev" v-model="jobForm.abbrev" placeholder="Code" maxlength="10" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="2" class="mb-50">
                  <b-button variant="primary" block :disabled="savingJobRow" @click="saveJobRow">
                    <b-spinner v-if="savingJobRow" small class="mr-25" />
                    Add
                  </b-button>
                </b-col>
              </b-row>
            </b-card>

            <div class="admin-master-data__table-toolbar d-flex flex-wrap align-items-center justify-content-between mb-50">
              <b-form-input
                v-model="jobSearch"
                size="sm"
                placeholder="Search title or abbrev…"
                class="admin-master-data__search"
              />
              <small class="text-muted">{{ filteredJobRows.length }} of {{ jobRows.length }}</small>
            </div>

            <b-overlay :show="loadingJobRows" rounded spinner-small class="admin-master-data__table-wrap">
              <b-table
                v-if="filteredJobRows.length"
                :items="filteredJobRows"
                :fields="visibleConfigFields"
                small
                responsive
                sticky-header="280px"
                class="admin-master-data__table mb-0"
              >
                <template #cell(actions)="data">
                  <b-button
                    v-if="canDelete"
                    size="sm"
                    variant="outline-danger"
                    :disabled="deletingId === data.item.id"
                    @click="removeRow(data.item)"
                  >
                    <b-spinner v-if="deletingId === data.item.id" small />
                    <feather-icon v-else icon="Trash2Icon" size="14" />
                  </b-button>
                </template>
              </b-table>
              <admin-empty-state
                v-else-if="!loadingJobRows"
                icon="LayersIcon"
                :title="jobSearch ? 'No matches' : 'No values for this type'"
                :description="jobSearch
                  ? 'Try a different search term.'
                  : 'Add a title (and optional abbrev) above — it will appear in job dropdowns.'"
              />
            </b-overlay>
          </section>

          <!-- Custom config -->
          <section v-show="activeSection === 'custom'" aria-labelledby="md-custom-title">
            <admin-section-header
              id="md-custom-title"
              title="Custom config"
              description="Advanced admin_config rows for any module and parameter."
              icon="SlidersIcon"
            >
              <template #actions>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="loadingCustom"
                  @click="loadCustomRows"
                >
                  <b-spinner v-if="loadingCustom" small />
                  <feather-icon v-else icon="RefreshCwIcon" size="14" class="mr-25" />
                  Load
                </b-button>
              </template>
            </admin-section-header>

            <b-alert variant="warning" show class="py-75 mb-1 small">
              <div class="d-flex align-items-start">
                <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50 flex-shrink-0 mt-25" />
                <span>
                  Prefer the dedicated sections above when possible. Wrong module/param values can break dropdowns across the app. Deletes cannot be undone.
                </span>
              </div>
            </b-alert>

            <b-card v-if="canCreate" body-class="p-75 mb-1" class="admin-master-data__form-card">
              <h6 class="admin-master-data__form-title mb-75">
                <feather-icon icon="PlusCircleIcon" size="15" class="mr-25" />
                Add config row
              </h6>
              <b-row class="align-items-end">
                <b-col cols="12" md="3" class="mb-50">
                  <b-form-group
                    label="Module"
                    label-for="custom-module"
                    label-class="font-weight-bold small"
                    description="e.g. jobs, expense — select or type"
                    class="mb-0"
                  >
                    <v-select
                      id="custom-module"
                      v-model="customForm.moduleName"
                      :options="customModuleOptions"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      taggable
                      :create-option="createCustomOption"
                      placeholder="jobs"
                      class="admin-master-data__v-select"
                      @option:created="onCustomModuleCreated"
                      @input="onCustomModuleChange"
                    >
                      <template #no-options="{ search }">
                        <span v-if="search">Use "{{ search }}"</span>
                        <span v-else>Type a module name</span>
                      </template>
                    </v-select>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="3" class="mb-50">
                  <b-form-group
                    label="Param"
                    label-for="custom-param"
                    label-class="font-weight-bold small"
                    description="Filtered by module — select or type"
                    class="mb-0"
                  >
                    <v-select
                      id="custom-param"
                      v-model="customForm.paramName"
                      :options="customParamOptions"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      taggable
                      :create-option="createCustomOption"
                      :placeholder="customParamPlaceholder"
                      :disabled="!hasCustomModule"
                      class="admin-master-data__v-select"
                      @option:created="onCustomParamCreated"
                      @input="onCustomParamChange"
                    >
                      <template #no-options="{ search }">
                        <span v-if="!hasCustomModule">Select a module first</span>
                        <span v-else-if="search">Use "{{ search }}"</span>
                        <span v-else>Type a parameter name</span>
                      </template>
                    </v-select>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="2" class="mb-50">
                  <b-form-group label="Title" label-for="custom-title" label-class="font-weight-bold small" class="mb-0">
                    <b-form-input id="custom-title" v-model="customForm.title" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="2" class="mb-50">
                  <b-form-group label="Abbrev" label-for="custom-abbrev" label-class="font-weight-bold small" class="mb-0">
                    <b-form-input id="custom-abbrev" v-model="customForm.abbrev" maxlength="10" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="2" class="mb-50">
                  <b-button variant="primary" block :disabled="savingCustom || loadingCustom" @click="saveCustomRow">
                    <b-spinner v-if="savingCustom" small class="mr-25" />
                    Add
                  </b-button>
                </b-col>
              </b-row>
            </b-card>

            <div class="admin-master-data__table-toolbar d-flex flex-wrap align-items-center justify-content-between mb-50">
              <b-form-input
                v-model="customSearch"
                size="sm"
                placeholder="Search title or abbrev…"
                class="admin-master-data__search"
              />
              <small class="text-muted">{{ filteredCustomRows.length }} of {{ customRows.length }}</small>
            </div>

            <b-overlay :show="loadingCustom" rounded spinner-small class="admin-master-data__table-wrap">
              <b-table
                v-if="filteredCustomRows.length"
                :items="filteredCustomRows"
                :fields="visibleConfigFields"
                small
                responsive
                sticky-header="280px"
                class="admin-master-data__table mb-0"
              >
                <template #cell(actions)="data">
                  <b-button
                    v-if="canDelete"
                    size="sm"
                    variant="outline-danger"
                    :disabled="deletingId === data.item.id"
                    @click="removeRow(data.item)"
                  >
                    <b-spinner v-if="deletingId === data.item.id" small />
                    <feather-icon v-else icon="Trash2Icon" size="14" />
                  </b-button>
                </template>
              </b-table>
              <admin-empty-state
                v-else-if="!loadingCustom"
                icon="SlidersIcon"
                :title="customEmptyTitle"
                :description="customEmptyDescription"
              />
            </b-overlay>
          </section>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCol,
  BCollapse,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BOverlay,
  BRow,
  BSpinner,
  BTable,
  VBToggle,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import AdminSectionHeader from '@/views/apps/admin/components/AdminSectionHeader.vue'
import AdminEmptyState from '@/views/apps/admin/components/AdminEmptyState.vue'
import adminConfigService from '@/services/admin-config-service'
import clientService from '@/services/client-service'
import locationService from '@/services/location-service'
import {
  canCreateMasterData,
  canDeleteMasterData,
  canManageMasterData,
  getCurrentEmpId,
} from '@/utils/admin-settings-permissions'

const EXOTEL_MODULE = 'induslabs'
const EXOTEL_PARAM = 'Voice Call'
const JOBS_MODULE = 'jobs'
const MASTER_DATA_SECTION_KEYS = ['exotel', 'clients', 'locations', 'jobs', 'custom']

/** Common admin_config modules used across the app. User can still type any string. */
const COMMON_CUSTOM_MODULES = [
  'jobs',
  'expense',
  'invoice',
  'reimbursement',
  'ai-calling',
  'attendance',
  'infra',
  'induslabs',
  'exotel',
  'project',
  'menu',
  'kpmg-rpo',
]

/** Known param_name values per module (from dedicated Admin Settings + getConfig callers). */
const KNOWN_PARAMS_BY_MODULE = {
  jobs: ['category', 'vertical', 'payroll', 'jobType', 'jobStatus'],
  expense: ['category', 'sub_category', 'payment_mode', 'businessUnit'],
  reimbursement: ['category', 'sub_category', 'payment_mode', 'vertical'],
  invoice: ['columnTypes', 'gst', 'sac', 'payment_terms', 'credit_period', 'tds'],
  'ai-calling': ['settings'],
  attendance: ['status'],
  induslabs: [EXOTEL_PARAM],
  infra: ['clients', 'positionList', 'models', 'sectors'],
  exotel: [EXOTEL_PARAM],
  project: ['project', 'location', 'state'],
  menu: ['source', 'qualification'],
  'kpmg-rpo': ['business', 'poc', 'level'],
}

const HARVEST_SKIP_KEYS = new Set(['status', 'message', 'error', 'code', 'data'])

function harvestKeyToParam(key) {
  const raw = String(key || '').trim()
  if (!raw || HARVEST_SKIP_KEYS.has(raw.toLowerCase())) return ''
  return raw.replace(/List$/i, '')
}

function unwrapGroupedConfig(res) {
  if (!res || typeof res !== 'object') return null
  const candidates = [res, res.data, res.data && res.data.data]
  for (let i = 0; i < candidates.length; i += 1) {
    const cand = candidates[i]
    if (!cand || typeof cand !== 'object' || Array.isArray(cand)) continue
    const keys = Object.keys(cand).filter(k => !HARVEST_SKIP_KEYS.has(k.toLowerCase()))
    const hasLists = keys.some(k => Array.isArray(cand[k]))
    if (hasLists) return cand
  }
  return null
}

function uniqueSortedStrings(list) {
  const seen = new Set()
  const out = []
  ;(list || []).forEach(item => {
    const val = String(item == null ? '' : item).trim()
    if (!val) return
    const key = val.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    out.push(val)
  })
  return out.sort((a, b) => a.localeCompare(b))
}

function normalizeSelectString(value) {
  if (value == null) return ''
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim()
  if (typeof value === 'object') {
    const raw = value.label ?? value.title ?? value.value ?? value.text ?? ''
    return String(raw).trim()
  }
  return String(value).trim()
}

export default {
  name: 'AdminMasterDataPanel',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCol,
    BCollapse,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BOverlay,
    BRow,
    BSpinner,
    BTable,
    AdminSectionHeader,
    AdminEmptyState,
    vSelect,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  props: {
    embedded: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeSection: 'exotel',
      sections: [
        { key: 'exotel', title: 'DID Assignment', icon: 'PhoneIcon' },
        { key: 'clients', title: 'Clients', icon: 'BriefcaseIcon' },
        { key: 'locations', title: 'Locations', icon: 'MapPinIcon' },
        { key: 'jobs', title: 'Job data', icon: 'LayersIcon' },
        { key: 'custom', title: 'Custom config', icon: 'SlidersIcon' },
      ],
      statusMsg: '',
      statusVariant: 'success',
      deletingId: null,
      loadingDids: false,
      savingDid: false,
      showDidForm: false,
      didFormTouched: false,
      exotelRows: [],
      didForm: { empId: '', callerId: '' },
      didSearch: '',
      didFields: [
        { key: 'title', label: 'Emp ID', sortable: true },
        { key: 'abbrev', label: 'Caller ID', sortable: true },
        { key: 'actions', label: '', class: 'text-right' },
      ],
      loadingClients: false,
      savingClient: false,
      clientFormTouched: false,
      clientForm: { name: '' },
      clients: [],
      clientSearch: '',
      clientFields: [{ key: 'title', label: 'Client', sortable: true }],
      loadingLocations: false,
      savingLocation: false,
      locationFormTouched: false,
      locationForm: { name: '' },
      locations: [],
      locationSearch: '',
      locationFields: [{ key: 'title', label: 'Location', sortable: true }],
      jobForm: { paramName: 'category', title: '', abbrev: '' },
      jobParamOptions: [
        { value: 'category', text: 'Category' },
        { value: 'vertical', text: 'Vertical' },
        { value: 'payroll', text: 'Payroll' },
        { value: 'jobType', text: 'Job type' },
        { value: 'jobStatus', text: 'Job status' },
      ],
      loadingJobRows: false,
      savingJobRow: false,
      jobFormTouched: false,
      jobRows: [],
      jobSearch: '',
      customForm: { moduleName: 'jobs', paramName: 'category', title: '', abbrev: '' },
      loadingCustom: false,
      savingCustom: false,
      customRows: [],
      customSearch: '',
      customCatalogLoaded: false,
      customLoadSeq: 0,
      discoveredModules: [],
      discoveredParamsByModule: {},
      configFields: [
        { key: 'title', label: 'Title', sortable: true },
        { key: 'abbrev', label: 'Abbrev', sortable: true },
        { key: 'actions', label: '', class: 'text-right' },
      ],
    }
  },
  computed: {
    canAccess() {
      return canManageMasterData(this.$can.bind(this))
    },
    canCreate() {
      return canCreateMasterData(this.$can.bind(this))
    },
    canDelete() {
      return canDeleteMasterData(this.$can.bind(this))
    },
    visibleDidFields() {
      return this.canDelete ? this.didFields : this.didFields.filter(f => f.key !== 'actions')
    },
    visibleConfigFields() {
      return this.canDelete ? this.configFields : this.configFields.filter(f => f.key !== 'actions')
    },
    filteredExotelRows() {
      return this.filterRows(this.exotelRows, this.didSearch, ['title', 'abbrev'])
    },
    filteredClients() {
      return this.filterRows(this.clients, this.clientSearch, ['title', 'client_name', 'name'])
    },
    filteredLocations() {
      return this.filterRows(this.locations, this.locationSearch, ['title', 'name'])
    },
    filteredJobRows() {
      return this.filterRows(this.jobRows, this.jobSearch, ['title', 'abbrev'])
    },
    filteredCustomRows() {
      return this.filterRows(this.customRows, this.customSearch, ['title', 'abbrev'])
    },
    customModuleOptions() {
      return uniqueSortedStrings([
        ...COMMON_CUSTOM_MODULES,
        ...this.discoveredModules,
        this.customForm.moduleName,
      ])
    },
    hasCustomModule() {
      return !!normalizeSelectString(this.customForm.moduleName)
    },
    customParamPlaceholder() {
      return this.hasCustomModule ? 'Select or type a param' : 'Select a module first'
    },
    customParamOptions() {
      return this.paramsForModule(this.customForm.moduleName)
    },
    hasCustomParam() {
      return !!normalizeSelectString(this.customForm.paramName)
    },
    customPairKey() {
      if (!this.hasCustomModule || !this.hasCustomParam) return ''
      return `${normalizeSelectString(this.customForm.moduleName).toLowerCase()}::${normalizeSelectString(this.customForm.paramName).toLowerCase()}`
    },
    customEmptyTitle() {
      if (!this.hasCustomModule || !this.hasCustomParam) return 'Select module and param'
      return 'No rows for this pair'
    },
    customEmptyDescription() {
      if (!this.hasCustomModule || !this.hasCustomParam) {
        return 'Choose a module and param to see existing admin_config rows.'
      }
      return 'Nothing is configured yet for this module and param. Add a title above if you need a new row.'
    },
  },
  watch: {
    customPairKey() {
      this.autoLoadCustomRowsIfVisible()
    },
    '$route.query.section'() {
      this.syncSectionFromRoute()
    },
    activeSection(key) {
      if (key !== 'exotel') {
        this.cancelDidForm()
      }
      if (key === 'jobs' && !this.jobRows.length && !this.loadingJobRows) {
        this.loadJobRows()
      }
      if (key === 'custom') {
        this.loadCustomCatalog()
        this.autoLoadCustomRows()
      }
    },
  },
  created() {
    this.syncSectionFromRoute()
    if (this.canAccess) {
      this.loadExotelRows()
      this.loadClients()
      this.loadLocations()
      if (this.activeSection === 'custom') {
        this.loadCustomCatalog()
        this.autoLoadCustomRows()
      }
    }
  },
  methods: {
    openDidForm() {
      this.showDidForm = true
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('#did-emp-id')
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    cancelDidForm() {
      this.showDidForm = false
      this.didForm = { empId: '', callerId: '' }
      this.didFormTouched = false
    },
    syncSectionFromRoute() {
      const q = this.$route.query && this.$route.query.section
      if (q && MASTER_DATA_SECTION_KEYS.includes(q)) {
        this.activeSection = q
      }
    },
    selectSection(key) {
      if (!key || this.activeSection === key) return
      this.activeSection = key
      if (this.$route.query.section === key) return
      this.$router.replace({
        name: 'apps-admin-settings',
        query: { ...this.$route.query, tab: 'master-data', section: key },
      }).catch(() => {})
    },
    sectionCount(key) {
      if (key === 'exotel') return this.exotelRows.length || 0
      if (key === 'clients') return this.clients.length || 0
      if (key === 'locations') return this.locations.length || 0
      if (key === 'jobs') return this.jobRows.length || 0
      if (key === 'custom') return this.customRows.length || 0
      return 0
    },
    filterRows(list, query, fields) {
      const q = (query || '').trim().toLowerCase()
      if (!q) return list
      return list.filter(item => fields.some(f => String(item[f] || '').toLowerCase().includes(q)))
    },
    toastStatus(message, variant = 'success') {
      const toastVariant = variant === 'danger' ? 'danger' : variant === 'warning' ? 'warning' : 'success'
      this.$bvToast.toast(message, {
        title: 'Master data',
        variant: toastVariant,
        solid: true,
        autoHideDelay: variant === 'success' ? 4000 : 6000,
      })
      if (variant === 'danger' || variant === 'warning') {
        this.statusMsg = message
        this.statusVariant = variant
      } else {
        this.statusMsg = ''
      }
    },
    masterDataLabel(item, fields = ['title', 'name', 'client_name']) {
      for (let i = 0; i < fields.length; i += 1) {
        const value = item?.[fields[i]]
        if (value != null && String(value).trim()) return String(value).trim()
      }
      return ''
    },
    hasDuplicateMasterDataName(list, name, fields) {
      const normalized = (name || '').trim().toLowerCase()
      if (!normalized) return false
      return list.some(item => this.masterDataLabel(item, fields).toLowerCase() === normalized)
    },
    isIdSequenceError(res) {
      const err = res?.error
      if (err?.fields?.id != null) return true
      const detail = err?.parent?.detail || err?.original?.detail || ''
      if (/key \(id\)=/i.test(detail)) return true
      return res?.code === 'CLIENTS_ID_SEQUENCE_OUT_OF_SYNC'
    },
    isDuplicateMasterDataError(res) {
      if (!res || typeof res !== 'object') return false
      if (this.isIdSequenceError(res)) return false
      const err = res.error
      if (err?.fields?.client_name != null) return true
      const payload = JSON.stringify(res).toLowerCase()
      if (payload.includes('client_name') && payload.includes('already exists')) return true
      const message = String(res.message || '').toLowerCase()
      return res.status === 'FAILED' && message === 'validation error'
    },
    masterDataSaveErrorMessage(res, entityLabel, fallback) {
      if (this.isIdSequenceError(res)) {
        return 'Could not save: database ID counter is out of sync. Try again — if it persists, contact admin.'
      }
      if (this.isDuplicateMasterDataError(res)) return `${entityLabel} already exists.`
      return res?.message || fallback
    },
    createCustomOption(value) {
      return normalizeSelectString(value)
    },
    rememberModule(moduleName) {
      const name = normalizeSelectString(moduleName)
      if (!name) return
      const exists = this.discoveredModules.some(item => item.toLowerCase() === name.toLowerCase())
      if (!exists) this.discoveredModules = [...this.discoveredModules, name]
    },
    rememberParam(moduleName, paramName) {
      const module = normalizeSelectString(moduleName)
      const param = normalizeSelectString(paramName)
      if (!module || !param) return
      this.rememberModule(module)
      const key = module.toLowerCase()
      const current = this.discoveredParamsByModule[key] || []
      if (current.some(item => item.toLowerCase() === param.toLowerCase())) return
      this.discoveredParamsByModule = {
        ...this.discoveredParamsByModule,
        [key]: [...current, param],
      }
    },
    rememberCustomPair(moduleName, paramName) {
      this.rememberParam(moduleName, paramName)
    },
    paramsForModule(moduleName) {
      const name = normalizeSelectString(moduleName)
      if (!name) return []
      const key = name.toLowerCase()
      const known = KNOWN_PARAMS_BY_MODULE[name] || KNOWN_PARAMS_BY_MODULE[key] || []
      const discovered = this.discoveredParamsByModule[key] || []
      return uniqueSortedStrings([...known, ...discovered])
    },
    isParamValidForModule(moduleName, paramName) {
      const param = normalizeSelectString(paramName)
      if (!param) return false
      return this.paramsForModule(moduleName).some(item => item.toLowerCase() === param.toLowerCase())
    },
    syncParamToModule(moduleName) {
      if (!normalizeSelectString(moduleName)) {
        this.customForm.paramName = ''
        this.customRows = []
        return
      }
      if (!this.isParamValidForModule(moduleName, this.customForm.paramName)) {
        this.customForm.paramName = ''
        this.customRows = []
      }
    },
    harvestGroupedConfig(moduleName, res) {
      const grouped = unwrapGroupedConfig(res)
      if (!grouped) return
      this.rememberModule(moduleName)
      Object.keys(grouped).forEach(key => {
        if (!Array.isArray(grouped[key])) return
        const param = harvestKeyToParam(key)
        if (param) this.rememberParam(moduleName, param)
      })
    },
    harvestRows(rows, fallbackModule, fallbackParam) {
      ;(rows || []).forEach(row => {
        const moduleName = row?.module_name || row?.moduleName || fallbackModule
        const paramName = row?.param_name || row?.paramName || fallbackParam
        this.rememberCustomPair(moduleName, paramName)
      })
      if (fallbackModule && fallbackParam) {
        this.rememberCustomPair(fallbackModule, fallbackParam)
      }
    },
    async harvestModuleParams(moduleName) {
      const name = normalizeSelectString(moduleName)
      if (!name) return
      const key = name.toLowerCase()
      let fetcher = null
      if (key === 'expense' || key === 'reimbursement') fetcher = () => adminConfigService.getConfigExpense()
      else if (key === 'infra') fetcher = () => adminConfigService.getConfigInfra()
      else if (key === 'jobs') fetcher = () => adminConfigService.getJobsDropdownList()
      else if (key === 'project') fetcher = () => adminConfigService.getProjectDropdownList()
      else if (key === 'menu') fetcher = () => adminConfigService.getATSDropdownList()
      else if (key === 'kpmg-rpo') fetcher = () => adminConfigService.getKPMGRPOConfig()
      if (!fetcher) return
      try {
        const res = await fetcher()
        this.harvestGroupedConfig(name, res)
      } catch (e) {
        // Known params for this module still apply.
      }
    },
    onCustomModuleCreated(value) {
      this.rememberModule(value)
    },
    onCustomParamCreated(value) {
      this.rememberParam(this.customForm.moduleName, value)
    },
    onCustomModuleChange(value) {
      const nextModule = normalizeSelectString(value)
      this.customForm.moduleName = nextModule
      this.rememberModule(nextModule)
      this.syncParamToModule(nextModule)
      this.harvestModuleParams(nextModule)
    },
    onCustomParamChange(value) {
      this.customForm.paramName = normalizeSelectString(value)
      if (this.hasCustomModule) {
        this.rememberParam(this.customForm.moduleName, this.customForm.paramName)
      }
    },
    async loadCustomCatalog() {
      if (this.customCatalogLoaded) return
      this.customCatalogLoaded = true
      await this.harvestModuleParams(this.customForm.moduleName)
    },
    autoLoadCustomRowsIfVisible() {
      if (this.activeSection !== 'custom' || !this.canAccess) return
      this.autoLoadCustomRows()
    },
    autoLoadCustomRows() {
      const moduleName = normalizeSelectString(this.customForm.moduleName)
      const paramName = normalizeSelectString(this.customForm.paramName)
      if (!moduleName || !paramName) {
        this.customRows = []
        return
      }
      this.loadCustomRows({ silent: true })
    },
    findDuplicateCustomRow(title, abbrev) {
      const titleKey = (title || '').trim().toLowerCase()
      const abbrevKey = (abbrev || '').trim().toLowerCase()
      let titleMatch = null
      let abbrevMatch = null
      ;(this.customRows || []).forEach(row => {
        if (titleKey && this.masterDataLabel(row, ['title']).toLowerCase() === titleKey) {
          titleMatch = row
        }
        if (abbrevKey && String(row?.abbrev || '').trim().toLowerCase() === abbrevKey) {
          abbrevMatch = row
        }
      })
      return { titleMatch, abbrevMatch }
    },
    adminConfigPayload(module_name, param_name, title, abbrev) {
      const empId = getCurrentEmpId()
      return {
        module_name,
        param_name,
        title: (title || '').trim(),
        abbrev: (abbrev || '').trim(),
        value: 0,
        added_by: empId,
        updated_by: empId,
      }
    },
    async loadExotelRows() {
      this.loadingDids = true
      try {
        const res = await adminConfigService.getConfig(EXOTEL_MODULE, EXOTEL_PARAM)
        this.exotelRows = Array.isArray(res?.data) ? res.data : []
      } catch (e) {
        this.exotelRows = []
        this.toastStatus('Could not load Exotel DIDs.', 'danger')
      } finally {
        this.loadingDids = false
      }
    },
    async saveDid() {
      if (!this.canCreate) return
      this.didFormTouched = true
      const title = (this.didForm.empId || '').trim()
      const abbrev = (this.didForm.callerId || '').trim()
      if (!title || !abbrev) {
        this.toastStatus('Emp ID and Caller ID are required.', 'warning')
        return
      }
      this.savingDid = true
      try {
        const res = await adminConfigService.create(
          this.adminConfigPayload(EXOTEL_MODULE, EXOTEL_PARAM, title, abbrev),
        )
        if (res?.status === 'OK') {
          this.didForm = { empId: '', callerId: '' }
          this.didFormTouched = false
          this.showDidForm = false
          this.toastStatus('DID assigned.')
          await this.loadExotelRows()
        } else {
          this.toastStatus(res?.message || 'Failed to assign DID.', 'danger')
        }
      } finally {
        this.savingDid = false
      }
    },
    async loadClients() {
      this.loadingClients = true
      try {
        const res = await clientService.getClients()
        this.clients = Array.isArray(res?.data) ? res.data : []
      } catch (e) {
        this.clients = []
      } finally {
        this.loadingClients = false
      }
    },
    async saveClient() {
      if (!this.canCreate) return
      this.clientFormTouched = true
      const name = (this.clientForm.name || '').trim()
      if (!name) {
        this.toastStatus('Client name is required.', 'warning')
        return
      }
      if (this.hasDuplicateMasterDataName(this.clients, name, ['title', 'client_name', 'name'])) {
        this.toastStatus('Client already exists.', 'warning')
        return
      }
      this.savingClient = true
      try {
        const res = await clientService.create({ client_name: name, client_logo: null })
        if (res?.status === 'OK') {
          this.clientForm.name = ''
          this.clientFormTouched = false
          this.toastStatus('Client added.')
          await this.loadClients()
        } else {
          this.toastStatus(this.masterDataSaveErrorMessage(res, 'Client', 'Failed to add client.'), 'danger')
        }
      } finally {
        this.savingClient = false
      }
    },
    async loadLocations() {
      this.loadingLocations = true
      try {
        const res = await locationService.getAll()
        this.locations = Array.isArray(res?.data) ? res.data : []
      } catch (e) {
        this.locations = []
      } finally {
        this.loadingLocations = false
      }
    },
    async saveLocation() {
      if (!this.canCreate) return
      this.locationFormTouched = true
      const name = (this.locationForm.name || '').trim()
      if (!name) {
        this.toastStatus('Location name is required.', 'warning')
        return
      }
      if (this.hasDuplicateMasterDataName(this.locations, name, ['title', 'name'])) {
        this.toastStatus('Location already exists.', 'warning')
        return
      }
      this.savingLocation = true
      try {
        const res = await locationService.create({ name })
        if (res?.status === 'OK') {
          this.locationForm.name = ''
          this.locationFormTouched = false
          this.toastStatus('Location added.')
          await this.loadLocations()
        } else {
          this.toastStatus(this.masterDataSaveErrorMessage(res, 'Location', 'Failed to add location.'), 'danger')
        }
      } finally {
        this.savingLocation = false
      }
    },
    async loadJobRows() {
      this.loadingJobRows = true
      try {
        const res = await adminConfigService.getConfig(JOBS_MODULE, this.jobForm.paramName)
        this.jobRows = Array.isArray(res?.data) ? res.data : []
      } catch (e) {
        this.jobRows = []
      } finally {
        this.loadingJobRows = false
      }
    },
    async saveJobRow() {
      if (!this.canCreate) return
      this.jobFormTouched = true
      const title = (this.jobForm.title || '').trim()
      if (!title) {
        this.toastStatus('Title is required.', 'warning')
        return
      }
      this.savingJobRow = true
      try {
        const res = await adminConfigService.create(
          this.adminConfigPayload(JOBS_MODULE, this.jobForm.paramName, title, this.jobForm.abbrev),
        )
        if (res?.status === 'OK') {
          this.jobForm.title = ''
          this.jobForm.abbrev = ''
          this.jobFormTouched = false
          this.toastStatus('Job master value added.')
          await this.loadJobRows()
        } else {
          this.toastStatus(res?.message || 'Failed to add value.', 'danger')
        }
      } finally {
        this.savingJobRow = false
      }
    },
    async loadCustomRows({ silent = false } = {}) {
      const moduleName = normalizeSelectString(this.customForm.moduleName)
      const paramName = normalizeSelectString(this.customForm.paramName)
      if (!moduleName || !paramName) {
        this.customRows = []
        if (!silent) this.toastStatus('Module and param are required.', 'warning')
        return
      }
      this.customLoadSeq += 1
      const seq = this.customLoadSeq
      this.loadingCustom = true
      try {
        const res = await adminConfigService.getConfig(moduleName, paramName)
        if (seq !== this.customLoadSeq) return
        this.customRows = Array.isArray(res?.data) ? res.data : []
        this.harvestRows(this.customRows, moduleName, paramName)
      } catch (e) {
        if (seq !== this.customLoadSeq) return
        this.customRows = []
      } finally {
        if (seq === this.customLoadSeq) this.loadingCustom = false
      }
    },
    async saveCustomRow() {
      if (!this.canCreate) return
      const moduleName = normalizeSelectString(this.customForm.moduleName)
      const paramName = normalizeSelectString(this.customForm.paramName)
      const title = (this.customForm.title || '').trim()
      const abbrev = (this.customForm.abbrev || '').trim()
      if (!moduleName || !paramName || !title) {
        this.toastStatus('Module, param, and title are required.', 'warning')
        return
      }
      this.savingCustom = true
      try {
        await this.loadCustomRows({ silent: true })
        const { titleMatch, abbrevMatch } = this.findDuplicateCustomRow(title, abbrev)
        if (titleMatch || abbrevMatch) {
          const parts = []
          if (titleMatch) parts.push(`title "${title}"`)
          if (abbrevMatch) parts.push(`abbrev "${abbrev}"`)
          this.toastStatus(
            `Already exists: ${parts.join(' and ')} for ${moduleName}/${paramName}. Check the list below.`,
            'warning',
          )
          return
        }
        const res = await adminConfigService.create(
          this.adminConfigPayload(moduleName, paramName, title, abbrev),
        )
        if (res?.status === 'OK') {
          this.rememberCustomPair(moduleName, paramName)
          this.customForm.title = ''
          this.customForm.abbrev = ''
          this.toastStatus('Config row added.')
          await this.loadCustomRows({ silent: true })
        } else {
          this.toastStatus(res?.message || 'Failed to add row.', 'danger')
        }
      } finally {
        this.savingCustom = false
      }
    },
    async removeRow(item) {
      if (!this.canDelete || !item?.id) return
      const ok = await this.$bvModal.msgBoxConfirm(
        'Delete this config row? This action cannot be undone.',
        {
          title: 'Confirm delete',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return
      this.deletingId = item.id
      try {
        const res = await adminConfigService.deleteConfig(item.id)
        if (res?.status === 'OK') {
          this.toastStatus('Deleted.')
          await Promise.all([
            this.loadExotelRows(),
            this.loadJobRows(),
            this.loadCustomRows(),
          ])
        } else {
          this.toastStatus(res?.message || 'Delete failed.', 'danger')
        }
      } finally {
        this.deletingId = null
      }
    },
  },
}
</script>

<style scoped>
.admin-master-data {
  font-size: 0.875rem;
}
.admin-master-data ::v-deep .admin-section-header__title {
  font-size: 1.125rem;
}
.admin-master-data ::v-deep .admin-section-header__desc {
  font-size: 0.875rem;
}
.admin-master-data ::v-deep .col-form-label,
.admin-master-data ::v-deep .form-group label,
.admin-master-data ::v-deep label.small {
  font-size: 0.875rem;
}
.admin-master-data ::v-deep .form-text,
.admin-master-data ::v-deep small {
  font-size: 0.8125rem;
}
.admin-master-data ::v-deep .form-control,
.admin-master-data ::v-deep .form-control-sm,
.admin-master-data ::v-deep .custom-select {
  font-size: 0.875rem;
}
.admin-master-data ::v-deep .admin-master-data__table tbody td {
  font-size: 0.875rem;
}
.admin-master-data__emp-chip {
  font-size: 0.875rem;
  font-weight: 600;
}
.admin-master-data__nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  padding: 0.35rem;
  background: #fafafa;
}
@media (max-width: 767.98px) {
  .admin-master-data__nav {
    flex-direction: row;
    flex-wrap: wrap;
  }
}
.admin-master-data__nav-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: none;
  border-radius: 0.357rem;
  background: transparent;
  color: #6e6b7b;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
@media (max-width: 767.98px) {
  .admin-master-data__nav-item {
    width: auto;
    flex: 1 1 calc(50% - 0.25rem);
  }
}
.admin-master-data__nav-item:hover {
  background: rgba(115, 103, 240, 0.08);
  color: #7367f0;
}
.admin-master-data__nav-item:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 2px;
}
.admin-master-data__nav-item--active {
  background: #7367f0;
  color: #fff;
}
.admin-master-data__nav-item--active .admin-master-data__nav-badge {
  background: rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}
.admin-master-data__nav-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-master-data__nav-badge {
  font-size: 0.75rem;
  min-width: 1.25rem;
}
.admin-master-data__help-toggle {
  font-size: 0.8125rem;
  color: #b9b9c3;
}
.admin-master-data__form-card {
  border: 1px dashed #d8d6de;
  background: #fcfcfd;
}
.admin-master-data__form-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #5e5873;
  display: flex;
  align-items: center;
}
.admin-master-data__search {
  max-width: 16rem;
  flex: 1 1 10rem;
}
.admin-master-data__table-toolbar {
  gap: 0.5rem;
}
.admin-master-data__table-wrap {
  min-height: 4rem;
}
.admin-master-data ::v-deep .admin-master-data__table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f3f2f7;
  border-bottom-width: 1px;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6e6b7b;
}
.admin-master-data ::v-deep .table.b-table-sticky-header {
  max-height: 280px;
}
.admin-master-data ::v-deep .admin-master-data__v-select {
  font-size: 0.875rem;
}
.admin-master-data ::v-deep .admin-master-data__v-select .vs__dropdown-toggle {
  min-height: 38px;
  padding: 0 8px;
  border-color: #d8d6de;
}
.admin-master-data ::v-deep .admin-master-data__v-select.vs--open .vs__dropdown-toggle,
.admin-master-data ::v-deep .admin-master-data__v-select .vs__dropdown-toggle:hover {
  border-color: #7367f0;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
